/** Grid/list view of the subfolders and files in the current storage folder, with per-item selection */

import { toast } from 'sonner'
import { Folder, FileIcon, Download, Trash2, Copy, HardDrive, Play, Laugh } from 'lucide-react'
import { cn } from '../../lib/utils'
import { API_BASE } from '../../lib/api-client'
import { formatBytes } from '../../lib/format-bytes'
import { useT, type TranslateFn } from '../../i18n'
import { useAppStore } from '../../stores/app-store'
import { cardClass } from '../ui/styles'
import { ExtractionBadge } from './extraction-badge'
import { SummaryBlock } from './summary-block'
import { DescriptionEditor } from './description-editor'
import { RegenerateSummaryButton } from './regenerate-summary-button'
import { isPreviewable } from './storage-preview-modal'
import { useUpdateUploadDescription, useRegenerateSummary } from '../../hooks/use-uploads'
import { useSetUploadCollection, type StorageFolderNode, type StorageFile } from '../../hooks/use-storage'

const fileUrl = (file: StorageFile) => `${API_BASE}/api/files/${file.fileKey}`

const checkboxClass = 'h-4 w-4 shrink-0 cursor-pointer rounded accent-accent'

const smallActionClass = 'inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-fg-subtle hover:bg-surface-hover hover:text-fg'

function folderMeta(t: TranslateFn, folder: StorageFolderNode): string {
  const parts: string[] = []
  const subs = folder.children.length
  if (subs > 0) parts.push(t(subs === 1 ? 'storage.folder.subfolderOne' : 'storage.folder.subfolderMany', { count: subs }))
  if (folder.fileCount > 0) parts.push(t(folder.fileCount === 1 ? 'storage.folder.fileOne' : 'storage.folder.fileMany', { count: folder.fileCount }))
  return parts.join(' · ') || t('storage.folder.empty')
}

function copyLink(t: TranslateFn, file: StorageFile) {
  const url = new URL(fileUrl(file), window.location.origin).toString()
  navigator.clipboard.writeText(url).then(
    () => toast.success(t('storage.file.linkCopied')),
    () => toast.error(t('storage.file.copyFailed')),
  )
}

// ── Shared item props ─────────────────────────────────────────────────────────

interface FolderItemProps {
  folder: StorageFolderNode
  selected: boolean
  onToggle: (id: string) => void
  onOpen: (id: string) => void
}

interface FileItemProps {
  file: StorageFile
  selected: boolean
  onToggle: (id: string) => void
  onDelete: (file: StorageFile) => void
  onPreview?: (file: StorageFile) => void
}

function SelectBox({ label, checked, onChange }: { label: string; checked: boolean; onChange: () => void }) {
  return <input type="checkbox" checked={checked} onChange={onChange} aria-label={label} className={checkboxClass} />
}

function FileActions({ file, onDelete }: { file: StorageFile; onDelete: (file: StorageFile) => void }) {
  const t = useT()
  const setCollection = useSetUploadCollection()
  const isMeme = file.collection === 'meme'
  const toggleMeme = () => {
    // Marking deletes the file's extracted text, summary, and search vectors; unmarking runs extraction again
    if (!window.confirm(t(isMeme ? 'storage.file.removeMemeConfirm' : 'storage.file.addMemeConfirm', { name: file.filename }))) return
    setCollection.mutate(
      { id: file.id, collection: isMeme ? null : 'meme' },
      {
        onSuccess: () => toast.success(t(isMeme ? 'storage.file.memeRemoved' : 'storage.file.memeAdded', { name: file.filename })),
        onError: (err) => toast.error(t('storage.error.generic', { message: err instanceof Error ? err.message : String(err) })),
      },
    )
  }
  return (
    <>
      <button
        type="button"
        onClick={toggleMeme}
        disabled={setCollection.isPending}
        aria-pressed={isMeme}
        className={cn(smallActionClass, isMeme && 'text-accent-text', 'disabled:cursor-not-allowed disabled:opacity-60')}
        aria-label={t(isMeme ? 'storage.file.removeMemeNamed' : 'storage.file.addMemeNamed', { name: file.filename })}
        title={t(isMeme ? 'storage.file.removeMeme' : 'storage.file.addMeme')}
      >
        <Laugh className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={() => copyLink(t, file)}
        className={smallActionClass}
        aria-label={t('storage.file.copyLinkNamed', { name: file.filename })}
        title={t('storage.file.copyLink')}
      >
        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      <a
        href={fileUrl(file)}
        download={file.filename}
        className={smallActionClass}
        aria-label={t('storage.file.downloadNamed', { name: file.filename })}
        title={t('storage.file.download')}
      >
        <Download className="h-3.5 w-3.5" aria-hidden="true" />
      </a>
      <button
        type="button"
        onClick={() => onDelete(file)}
        className={cn(smallActionClass, 'text-danger hover:bg-danger-soft hover:text-danger')}
        aria-label={t('storage.file.deleteNamed', { name: file.filename })}
        title={t('storage.file.delete')}
      >
        <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </>
  )
}

// ── Grid mode ─────────────────────────────────────────────────────────────────

function FolderCard({ folder, selected, onToggle, onOpen }: FolderItemProps) {
  const t = useT()
  return (
    <li className={cn(cardClass, 'flex items-center gap-2 px-3 py-2.5 hover:bg-surface-hover', selected && 'border-accent')}>
      <SelectBox label={t('storage.select.named', { name: folder.name })} checked={selected} onChange={() => onToggle(folder.id)} />
      <button
        type="button"
        onClick={() => onOpen(folder.id)}
        aria-label={t('storage.folder.openNamed', { name: folder.name })}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left"
      >
        <Folder className="h-5 w-5 shrink-0 text-accent-text" aria-hidden="true" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-fg">{folder.name}</span>
          <span className="block text-xs text-fg-subtle">{folderMeta(t, folder)}</span>
        </span>
      </button>
    </li>
  )
}

function FileCard({ file, selected, onToggle, onDelete, onPreview }: FileItemProps) {
  const t = useT()
  const locale = useAppStore((s) => s.locale)
  const isImage = file.contentType.startsWith('image/')
  const isVideo = file.contentType.startsWith('video/')
  const canPreview = isPreviewable(file.contentType) && !!onPreview
  const updateDesc = useUpdateUploadDescription()
  const regenSummary = useRegenerateSummary()

  const thumbnail = isImage ? (
    <img src={fileUrl(file)} alt="" className="h-full w-full object-cover" loading="lazy" />
  ) : (
    <span className="flex h-full items-center justify-center bg-surface-muted">
      {isVideo ? <Play className="h-7 w-7 text-accent-text" aria-hidden="true" /> : <FileIcon className="h-7 w-7 text-fg-subtle" aria-hidden="true" />}
    </span>
  )

  const saveDescription = (description: string | null) =>
    updateDesc.mutate(
      { id: file.id, description },
      { onError: (err) => toast.error(t('storage.description.saveFailed', { message: err instanceof Error ? err.message : String(err) })) },
    )

  const regenerate = () =>
    regenSummary.mutate(file.id, {
      onError: (err) => toast.error(t('storage.summary.regenerateFailed', { message: err instanceof Error ? err.message : String(err) })),
    })

  return (
    <li className={cn(cardClass, 'group relative flex flex-col overflow-hidden', selected && 'border-accent')}>
      {/* Only the media region opens the preview, so the summary/description controls below stay independent */}
      <div className="h-28">
        {canPreview ? (
          <button
            type="button"
            onClick={() => onPreview?.(file)}
            aria-label={t('storage.preview.openNamed', { name: file.filename })}
            className="block h-full w-full cursor-zoom-in"
          >
            {thumbnail}
          </button>
        ) : (
          thumbnail
        )}
      </div>

      <span className="absolute left-2 top-2 flex rounded-md bg-surface-raised p-1 shadow-sm">
        <SelectBox label={t('storage.select.named', { name: file.filename })} checked={selected} onChange={() => onToggle(file.id)} />
      </span>
      <span className="absolute right-2 top-2 flex gap-0.5 rounded-md bg-surface-raised p-0.5 opacity-100 shadow-sm transition-opacity md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100">
        <FileActions file={file} onDelete={onDelete} />
      </span>

      <div className="flex items-center justify-between gap-1 px-3 pt-2">
        <p className="truncate text-sm font-medium text-fg" title={file.filename}>
          {file.filename}
        </p>
        {file.collection !== 'meme' && (
          <RegenerateSummaryButton status={file.extractionStatus} lastSummaryAt={file.lastSummaryAt} pending={regenSummary.isPending} onClick={regenerate} />
        )}
      </div>

      <div className="flex items-center justify-between gap-2 px-3 pb-2">
        <span className="text-xs text-fg-subtle">{formatBytes(file.sizeBytes, locale)}</span>
        <ExtractionBadge status={file.extractionStatus} uploadId={file.id} />
      </div>

      <SummaryBlock
        summary={file.summary}
        status={file.extractionStatus}
        extractionMethod={file.extractionMethod}
        contentType={file.contentType}
        onGenerate={regenerate}
        generatePending={regenSummary.isPending}
      />

      <DescriptionEditor value={file.description} onSave={saveDescription} saving={updateDesc.isPending} />
    </li>
  )
}

// ── List mode ─────────────────────────────────────────────────────────────────

function FolderRow({ folder, selected, onToggle, onOpen }: FolderItemProps) {
  const t = useT()
  return (
    <li className={cn('flex items-center gap-3 rounded-md border bg-surface-raised px-3 py-2 hover:bg-surface-hover', selected ? 'border-accent' : 'border-border')}>
      <SelectBox label={t('storage.select.named', { name: folder.name })} checked={selected} onChange={() => onToggle(folder.id)} />
      <button
        type="button"
        onClick={() => onOpen(folder.id)}
        aria-label={t('storage.folder.openNamed', { name: folder.name })}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 text-left"
      >
        <Folder className="h-4 w-4 shrink-0 text-accent-text" aria-hidden="true" />
        <span className="flex-1 truncate text-sm font-medium text-fg">{folder.name}</span>
        <span className="hidden shrink-0 text-xs text-fg-subtle sm:inline">{folderMeta(t, folder)}</span>
      </button>
    </li>
  )
}

function FileRow({ file, selected, onToggle, onDelete, onPreview }: FileItemProps) {
  const t = useT()
  const locale = useAppStore((s) => s.locale)
  const canPreview = isPreviewable(file.contentType) && !!onPreview
  const isVideo = file.contentType.startsWith('video/')
  const icon = isVideo ? <Play className="h-4 w-4 shrink-0 text-accent-text" aria-hidden="true" /> : <FileIcon className="h-4 w-4 shrink-0 text-fg-subtle" aria-hidden="true" />

  return (
    <li className={cn('group flex items-center gap-3 rounded-md border bg-surface-raised px-3 py-2', selected ? 'border-accent' : 'border-border')}>
      <SelectBox label={t('storage.select.named', { name: file.filename })} checked={selected} onChange={() => onToggle(file.id)} />
      {canPreview ? (
        <button
          type="button"
          onClick={() => onPreview?.(file)}
          aria-label={t('storage.preview.openNamed', { name: file.filename })}
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 text-left hover:text-accent-text"
        >
          {icon}
          <span className="truncate text-sm text-fg">{file.filename}</span>
        </button>
      ) : (
        <span className="flex min-w-0 flex-1 items-center gap-3">
          {icon}
          <span className="truncate text-sm text-fg" title={file.description ?? undefined}>
            {file.filename}
          </span>
        </span>
      )}
      <span className="hidden shrink-0 text-xs text-fg-subtle sm:inline">{formatBytes(file.sizeBytes, locale)}</span>
      <ExtractionBadge status={file.extractionStatus} uploadId={file.id} />
      <span className="flex items-center gap-0.5 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100">
        <FileActions file={file} onDelete={onDelete} />
      </span>
    </li>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

interface StorageFileGridProps {
  /** Subfolders in current folder (from tree, filtered to current parent) */
  subfolders: StorageFolderNode[]
  files: StorageFile[]
  viewMode: 'grid' | 'list'
  selectedFileIds: ReadonlySet<string>
  selectedFolderIds: ReadonlySet<string>
  onToggleFile: (id: string) => void
  onToggleFolder: (id: string) => void
  onFolderOpen: (id: string) => void
  onFileDelete: (file: StorageFile) => void
  onFilePreview?: (file: StorageFile) => void
  isLoading: boolean
  /** True while a name filter is applied (changes the empty message) */
  filtered: boolean
  /** Empty-state messages for a view other than a folder (the meme collection) */
  emptyMessages?: { empty: string; hint: string; filtered: string }
}

export function StorageFileGrid({
  subfolders,
  files,
  viewMode,
  selectedFileIds,
  selectedFolderIds,
  onToggleFile,
  onToggleFolder,
  onFolderOpen,
  onFileDelete,
  onFilePreview,
  isLoading,
  filtered,
  emptyMessages,
}: StorageFileGridProps) {
  const t = useT()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16" role="status" aria-label={t('common.loading')}>
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-border border-t-accent" />
      </div>
    )
  }

  if (subfolders.length === 0 && files.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border-strong py-16 text-fg-subtle">
        <HardDrive className="h-8 w-8" aria-hidden="true" />
        <p className="text-sm">{filtered ? emptyMessages?.filtered ?? t('storage.emptyFiltered') : emptyMessages?.empty ?? t('storage.empty')}</p>
        {!filtered && <p className="text-xs">{emptyMessages?.hint ?? t('storage.emptyHint')}</p>}
      </div>
    )
  }

  const folderProps = (folder: StorageFolderNode) => ({
    folder,
    selected: selectedFolderIds.has(folder.id),
    onToggle: onToggleFolder,
    onOpen: onFolderOpen,
  })
  const fileProps = (file: StorageFile) => ({
    file,
    selected: selectedFileIds.has(file.id),
    onToggle: onToggleFile,
    onDelete: onFileDelete,
    onPreview: onFilePreview,
  })

  if (viewMode === 'list') {
    return (
      <ul className="space-y-1.5" aria-label={t('storage.items')}>
        {subfolders.map((folder) => (
          <FolderRow key={folder.id} {...folderProps(folder)} />
        ))}
        {files.map((file) => (
          <FileRow key={file.id} {...fileProps(file)} />
        ))}
      </ul>
    )
  }

  const sectionLabelClass = 'mb-2 text-xs font-semibold text-fg-subtle'
  return (
    <div className="space-y-5">
      {subfolders.length > 0 && (
        <section>
          <h2 className={sectionLabelClass}>{t('storage.folders')}</h2>
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" aria-label={t('storage.folders')}>
            {subfolders.map((folder) => (
              <FolderCard key={folder.id} {...folderProps(folder)} />
            ))}
          </ul>
        </section>
      )}
      {files.length > 0 && (
        <section>
          <h2 className={sectionLabelClass}>{t('storage.files')}</h2>
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" aria-label={t('storage.files')}>
            {files.map((file) => (
              <FileCard key={file.id} {...fileProps(file)} />
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}
